import { Hub } from '@aws-amplify/core';
const canImpersonate = __GMK_COGNITO_CAN_IMPERSONATE__;
const MAX_EVENTS = 1000; //after this many events nothing has happened
export const initialRoute = new URL(window.location.href, window.location.origin);
//setup cognito instance refer to @aws-amplify docs
//https://docs.amplify.aws/lib/client-configuration/configuring-amplify-categories/q/platform/js/#general-configuration
export const config = {
    region: 'us-east-1',
    userPoolId: __GMK_COGNITO_VUE_USERPOOL__,
    userPoolWebClientId: __GMK_COGNITO_VUE_CLIENTID__,
    oauth: {
        domain: __GMK_COGNITO_VUE_DOMAIN__,
        scope: [...(new Set([
                ...(__GMK_COGNITO_VUE_SCOPES__ || ['openid', 'profile', 'email']),
                ...(canImpersonate ? ['aws.cognito.signin.user.admin'] : []),
            ]))],
        redirectSignIn: new URL('/authorization-code/callback', window.location.origin).href,
        redirectSignOut: new URL(window.location.origin).href,
        responseType: 'code',
    },
};
const override_failure = __GMK_COGNITO_FAILURE__;
const stdFailure = async (error) => { console.error(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}):`, error); throw error; };
export const failure = override_failure || stdFailure;
const override_success = __GMK_COGNITO_SUCCESS__;
const stdSuccess = async (url) => { window.location.replace(url || window.location.origin); };
export const success = override_success || stdSuccess;
const override_signin = __GMK_COGNITO_SIGNIN__;
const stdSignin = async () => {
    var _a, _b, _c, _d, _e;
    if (!hasRedirectConfigured(config === null || config === void 0 ? void 0 : config.oauth)) {
        throw 'unable to identify config is using hosted-ui';
    }
    //get parts needed to build redirect hosted-ui url
    const current = new URL(window.location.href);
    const client_id = config === null || config === void 0 ? void 0 : config.userPoolWebClientId;
    const response_type = (_a = config === null || config === void 0 ? void 0 : config.oauth) === null || _a === void 0 ? void 0 : _a.responseType;
    const scope = (_d = (_c = (_b = config === null || config === void 0 ? void 0 : config.oauth) === null || _b === void 0 ? void 0 : _b.scope) === null || _c === void 0 ? void 0 : _c.join) === null || _d === void 0 ? void 0 : _d.call(_c, ' ');
    const redirect_uri = (_e = config === null || config === void 0 ? void 0 : config.oauth) === null || _e === void 0 ? void 0 : _e.redirectSignIn;
    const redirectSignInUrl = new URL(redirect_uri, window.location.origin);
    const isRedirect = current.pathname == redirectSignInUrl.pathname;
    //validate
    if (!client_id) {
        throw 'config.userPoolWebClientId missing from config';
    }
    if (!response_type) {
        throw 'config.oauth.responseType missing from config';
    }
    if (!scope) {
        throw 'config.oauth.scope missing from config';
    }
    if (!redirect_uri) {
        throw 'config.oauth.redirectSignIn missing from config';
    }
    if (isRedirect) {
        throw 'current url is the redirect url';
    }
    //create url
    const login_url = '/oauth2/authorize?' + [
        `client_id=${encodeURIComponent(client_id)}`,
        `response_type=${encodeURIComponent(response_type)}`,
        `scope=${encodeURIComponent(scope)}`,
        `redirect_uri=${encodeURIComponent(redirect_uri)}`,
        `state=${encodeURIComponent(client_id)}` //replaced during build if able
    ].join('&');
    const domain = config.oauth.domain.split('://').pop();
    const url = new URL(login_url, `https://${domain}`);
    //navigate to the hosted-ui url
    window.location.replace(url);
};
export const signin = override_signin || stdSignin;
/** determines if config has a redirect */
export const hasRedirectConfigured = (config) => !!(config === null || config === void 0 ? void 0 : config.redirectSignIn);
/**
 * Determines if user is currently on the callback redirect from authentication login.
 * @returns true if matches login redirect of supplied config
 */
export const isLoginRedirect = (current, actual = config) => {
    if (!actual) {
        return false;
    }
    try {
        const redirect = (actual === null || actual === void 0 ? void 0 : actual.oauth) && hasRedirectConfigured(actual.oauth) && actual.oauth.redirectSignIn;
        if (!redirect) {
            throw 'no oauth.redirectSignIn in auth configuration';
        }
        const redirectSignInUrl = new URL(redirect, window.location.origin);
        const isLoginRedirect = current.pathname == redirectSignInUrl.pathname;
        return isLoginRedirect;
    }
    catch (wut) {
        if (__GMK_COGNITO_VUE_DEBUG__) {
            console.warn(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): issue with login redirect logic`, wut);
        }
        return false;
    }
};
/** Determines if the user is accessing the "hidden" impersonation url */
export const isMasqUrl = (current) => {
    if (!current) {
        return false;
    }
    try {
        const impurl = new URL('masq', window.location.origin);
        const isMasqUrl = current.pathname == impurl.pathname;
        return isMasqUrl;
    }
    catch (wut) {
        if (__GMK_COGNITO_VUE_DEBUG__) {
            console.warn(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): issue with masq logic`, wut);
        }
        return false;
    }
};
/** Promise to check if sign-in event was a success */
export const signInEvent = !isLoginRedirect(initialRoute, config) || Promise.race([
    //try to listen to the hub events for sign-in success
    new Promise((successful, failed) => {
        const ignore = Hub.listen('auth', (data) => {
            var _a, _b;
            let eventCount = 0;
            if (eventCount++ > MAX_EVENTS) {
                failed('max_event_limit_reached');
            }
            //display messages to user
            if (__GMK_COGNITO_VUE_DEBUG__) {
                console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): cognito event received`, new Date(), JSON.stringify(data === null || data === void 0 ? void 0 : data.payload, null, 2));
            }
            //capture specific events
            switch ((_a = data === null || data === void 0 ? void 0 : data.payload) === null || _a === void 0 ? void 0 : _a.event) {
                //known successes
                case "cognitoHostedUI":
                case "signIn":
                    {
                        successful(true);
                        ignore(); //make successful and ignore any more messages
                    }
                    break;
                //known failures
                case "cognitoHostedUI_failure":
                case "signIn_failure":
                    {
                        if (__GMK_COGNITO_VUE_DEBUG__) {
                            console.error(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): failed payload`, data === null || data === void 0 ? void 0 : data.payload);
                        }
                        failed(((_b = data === null || data === void 0 ? void 0 : data.payload) === null || _b === void 0 ? void 0 : _b.message) || 'unknown error');
                        ignore(); //make failed and ignore any more messages
                    }
                    break;
            }
        });
    }),
    //we race to see if we can auth the user in a reasonable timeframe
    new Promise(resolve => setTimeout(resolve, 30 * 1000, false)),
]);
//# sourceMappingURL=authn.config.js.map